import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Waves, Menu, X, ArrowUpRight, Globe } from "lucide-react";
import { useSimplify } from "./simplify-context";

const LOCALES = [
  { code: "EN", to: "/" },
  { code: "FR", to: "/fr" },
  { code: "ES", to: "/es" },
];

function LangSwitcher() {
  const { pathname } = useLocation();
  const current = pathname.startsWith("/fr") ? "FR" : pathname.startsWith("/es") ? "ES" : "EN";
  return (
    <div data-testid="lang-switcher" className="flex items-center gap-1 rounded-full border border-slate-200 bg-white px-1 py-1">
      {LOCALES.map((l) => (
        <Link
          key={l.code}
          to={l.to}
          data-testid={`lang-${l.code.toLowerCase()}`}
          hrefLang={l.code.toLowerCase()}
          className={`rounded-full px-2.5 py-1 text-[11px] font-mono font-semibold tracking-wider transition-colors duration-300 ${
            current === l.code ? "bg-brand-navy text-white" : "text-slate-500 hover:text-brand-navy"
          }`}
        >
          {l.code}
        </Link>
      ))}
    </div>
  );
}

const LINKS = [
  { id: "nav-link-work", label: "How it works", href: "/#work" },
  { id: "nav-link-flow", label: "Before & after", href: "/#flow" },
  { id: "nav-link-pricing", label: "Pricing", to: "/pricing" },
  { id: "nav-link-journal", label: "Journal", to: "/journal" },
  { id: "nav-link-about", label: "About", to: "/about" },
];

function NavItem({ item, onClick, testId }) {
  const cls = "text-sm font-medium text-slate-600 hover:text-brand-teal transition-colors duration-300";
  if (item.to) {
    return <Link to={item.to} data-testid={testId} onClick={onClick} className={cls}>{item.label}</Link>;
  }
  return <a href={item.href} data-testid={testId} onClick={onClick} className={cls}>{item.label}</a>;
}

export default function Header() {
  const { setOpen } = useSimplify();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();
  const current = pathname.startsWith("/fr") ? "FR" : pathname.startsWith("/es") ? "ES" : "EN";
  const ctaLabel = { EN: "Show me where I can simplify", FR: "Montrez-moi où simplifier", ES: "Muéstreme dónde simplificar" }[current];

  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 h-16 sm:h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center">
          <img src="/oceandata-logo.png?v=4" alt="Ocean Data" className="h-11 sm:h-12 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
          {LINKS.map((l) => (
            <NavItem key={l.id} item={l} testId={l.id} />
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <LangSwitcher />
          <button
            data-testid="nav-cta-button"
            onClick={() => setOpen(true)}
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-brand-navy text-white text-sm font-semibold px-5 py-2.5 transition-all duration-300 hover:bg-brand-teal hover:-translate-y-0.5"
          >
            {ctaLabel}
            <ArrowUpRight size={15} />
          </button>
          <button
            data-testid="nav-mobile-menu-button"
            className="lg:hidden text-brand-navy p-2"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-md border-b border-slate-100"
            aria-label="Mobile"
          >
            <div className="px-6 py-5 flex flex-col gap-4">
              <LangSwitcher />
              {LINKS.map((l) => (
                <span key={l.id} className="text-base font-medium text-brand-navy">
                  <NavItem item={l} testId={`${l.id}-mobile`} onClick={() => setMobileOpen(false)} />
                </span>
              ))}
              <button
                data-testid="nav-cta-button-mobile"
                onClick={() => { setMobileOpen(false); setOpen(true); }}
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-brand-navy text-white text-sm font-semibold px-5 py-3"
              >
                {ctaLabel}
                <ArrowUpRight size={15} />
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
